import { FormStore, FormValidator, IJsonSchema, getValidationSchema } from '@estee/elc-form';
import { action, computed, observable } from 'mobx';
import { StringSchema, string as yupString } from 'yup';
import { FIELD_TYPE, INPUT_TYPE } from '../../../constants/Constants';
import { formatValidationSchema } from '../../../utils/AddressValidation';
import { IAddressSchemaProperties, IAddressSchema, IAddressSchemaProperty } from '../../../api/JsonSchemaApi';
import { InputFieldData } from '../../../utils/InputFieldData';
import { FEATURE } from '../../../constants/Features';
import { addPlusSignForE164Pattern } from '../../../utils/AddressFormUtils';
import Config from '../Config';
import { ITranslationSchema } from '../../../interfaces/ITranslationFields';
import { IAddressAttributes } from '../Address/Address';
import DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA from '../../../constants/DefaultAdditionalPropertiesSchema';

interface IAddressSchemaLoadingOptions {
  hasStates: boolean;
}

export interface ICustomValidator {
  pattern: string;
  errorMessage: string;
}

export interface ISchemaCustomValidator {
  customValidation?: ICustomValidator, required?: string
}

interface IMapInputFieldData {
  [key: string]: InputFieldData;
}

type CustomValidator = (configs: ICustomValidator) => StringSchema;

interface IBaseAddressFormStoreOptions<T> {
  initialValues: T;
}

export interface IBaseFormAttributes {
  [FIELD_TYPE.COUNTRY_ID]?: number;
}

type ValidationSchema = {
  required?: string;
  pattern?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
} | {
  customValidation: ICustomValidator
};

/*
 Abstract class that enables a form store to set the address schema, translations and validation handling
*/
abstract class BaseAddressFormStore<T extends IBaseFormAttributes> extends FormStore<T> {

  @observable public addressSchema: IAddressSchema;

  @observable private translations: ITranslationSchema = {};

  @observable private features: FEATURE[] = [];

  @observable config: Config;

  public stateSchemaPropertyField: IAddressSchemaProperty;

  constructor(config: Config, options: IBaseAddressFormStoreOptions<T>) {
    super({
      formValidator: new FormValidator(),
      initialValues: options.initialValues,
    });
    (this.formValidator as FormValidator).registerValidation('customValidation', this.validateCustomPattern);
    this.config = config;
  }

  @computed get useLegacySchema(): boolean {
    return !this.config?.jsonSchemasEnabled;
  }

  // helpful when additional configurations are needed prior running the address validation out of a view form.
  protected abstract runPreValidationHandling(): void;

  // useful to customize the validation handling of a field
  protected abstract getCustomValidation(fieldType: INPUT_TYPE, errorMessage: string, requiredMessage: string): ISchemaCustomValidator;

  // TODO: Fix backend schema and then remove this method completely (RCO-3141)
  private adaptSchemaForSelectedCountry = (
    schema: IAddressSchemaProperties,
    hasStates?: boolean,
  ): IAddressSchemaProperties => {
    const globalSchema = { ...schema };
    const hasStatesFieldWithLookupData = !!schema?.state?.additionalProperties?.lookupData;
    if (!hasStates && !hasStatesFieldWithLookupData) {
      this.stateSchemaPropertyField = this.stateSchemaPropertyField || globalSchema.state;
      delete globalSchema.state;
    } else {
      globalSchema.state = globalSchema.state || this.stateSchemaPropertyField || DEFAULT_ADDITIONAL_PROPERTIES_SCHEMA.state;
    }

    return globalSchema;
  };

  @computed
  public get hasLookupData(): boolean {
    return Object.values(this.addressSchema?.properties || {}).some(item => item.additionalProperties?.lookupData);
  }

  public getAddressValidationSchema(
    schema?: IAddressSchemaProperties,
  ): IJsonSchema {
    const {
      values: { [FIELD_TYPE.COUNTRY_ID]: countryId },
    } = this;

    const addressSchema = schema || this.addressSchema.properties;

    if (!addressSchema) {
      return null;
    }

    const globalSchema = this.adaptSchemaForSelectedCountry(addressSchema);
    return formatValidationSchema(globalSchema, countryId);
  }

  private setSchemaValidations(): void {
    const excludeFromValidation = [FIELD_TYPE.COUNTRY_ID];

    Object.keys(this.addressSchema?.properties).forEach((schemaFieldKey: keyof IAddressAttributes) => {
      const schemaField = this.addressSchema?.properties[schemaFieldKey];
      const field = schemaField ? schemaFieldKey : null;
      const isDisplayable = schemaField
        && schemaField.additionalProperties
        && schemaField.additionalProperties.x;

      if (field && !excludeFromValidation.find(item => item === schemaFieldKey) && isDisplayable) {
        const validations = getValidationSchema(this.addressSchema, schemaFieldKey, {
          minLengthMessage: '',
          patternMessage: '',
          requiredMessage: '',
        }) || {};
        this.setFieldValidations(field, validations);
      }
    });
  }

  getValidations(fieldType: FIELD_TYPE): ValidationSchema {
    const inputData = this.inputsData[fieldType];

    let validations: ValidationSchema = this.getCustomValidation(  
      inputData.inputType as INPUT_TYPE,
      inputData.validationMessages.patternMessage,
      inputData.isRequired ? inputData.validationMessages.patternMessage : '',
    );

    if (!validations) {
      validations = getValidationSchema(
        this.addressSchema,
        fieldType,
        inputData.validationMessages,
      );

      if (inputData.inputType === INPUT_TYPE.TEL) {
        const originalPattern = inputData.config.propertySchema.pattern;
        const message = validations[`pattern:${originalPattern}`];
        // overwrite phone pattern validation to allow "+" sign, this is temporary until BE schemas are updated
        validations[`pattern:${addPlusSignForE164Pattern(originalPattern)}`] = message;
        delete validations[`pattern:${originalPattern}`];
      }
    } else if (inputData.inputType === INPUT_TYPE.TEL) {
      validations.customValidation.pattern = `${addPlusSignForE164Pattern(validations.customValidation.pattern)}`;
    }

    return validations;
  }

  public validateAddressBySchema(addressformSchema: IAddressSchema): boolean {
    this.setAddressSchema(addressformSchema, {
      hasStates: false,
    });
    this.setSchemaValidations();
    this.runPreValidationHandling();
    this.runValidationSchemaSync();

    return this.isValid;
  }

  @action
  public setTranslations(translations: ITranslationSchema): void {
    this.translations = translations;
  }

  @action
  public setFeatures(features: FEATURE[]): void {
    this.features = features;
  }

  @action
  public setAddressSchema(schema: IAddressSchema, options: IAddressSchemaLoadingOptions = {
    hasStates: false,
  }): void {
    if (this.useLegacySchema) {
      this.addressSchema = this.getValidationSchemaLegacy(schema, options.hasStates) as IAddressSchema;
    } else {
      this.addressSchema = schema;
    }
  }

  @computed get fieldTypes(): FIELD_TYPE[] {
    const properties = this.addressSchema?.properties || {};

    return Object.keys(properties).reduce((acc, item) => [...acc, item], []);
  }

  @computed get inputsData(): IMapInputFieldData {
    const properties = this.addressSchema?.properties || {};

    return (Object.keys(properties) as Array<keyof typeof properties>).reduce((acc, item) => ({
      ...acc,
      [item]: new InputFieldData({
        features: this.features,
        fieldType: item as FIELD_TYPE,
        isRequired: !!this.addressSchema?.required.includes(item),
        propertySchema: properties[item],
        translations: this.translations,
      }),
    }), {});
  }

  private validateCustomPattern: CustomValidator = ({
    pattern = '',
    errorMessage = '',
  }) => {
    const regExp = new RegExp(pattern);
    return yupString().matches(regExp, errorMessage);
  };

  getValidationSchemaLegacy(schema: IAddressSchema, hasStates: boolean): IJsonSchema {
    const {
      values: { [FIELD_TYPE.COUNTRY_ID]: countryId },
    } = this;

    if (!schema) {
      return null;
    }

    const globalSchema = this.adaptSchemaForSelectedCountry(
      schema.properties,
      hasStates,
    );

    return formatValidationSchema(globalSchema, countryId);
  }
}

export default BaseAddressFormStore;
